import { useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { Check, Video } from "lucide-react";

import { Icon } from "@/lib/Icon";
import { track } from "@/lib/analytics";
import type {
  Appointment,
  GuidedSelfInjectionState,
  GuidedSelfInjectionStatus,
} from "@/types/api";
import { clinicianInitials, renderableClinicianPhoto } from "../clinicianPhoto";
import { GuidedInjectionBookingDialog } from "./GuidedInjectionBookingDialog";
import { GuidedInjectionDetailsDrawer } from "./GuidedInjectionDetailsDrawer";

interface GuidedSelfInjectionCardProps {
  state: GuidedSelfInjectionState & {
    status: GuidedSelfInjectionStatus;
    experiment: NonNullable<GuidedSelfInjectionState["experiment"]>;
    presentation: NonNullable<GuidedSelfInjectionState["presentation"]>;
    preparation: NonNullable<GuidedSelfInjectionState["preparation"]>;
  };
}

const PUBLIC_NURSE_NAME = "Jasmine W.";

const BOOKING_UNAVAILABLE_COPY: Record<string, string> = {
  booking_not_configured:
    "Guided injection scheduling is still being set up. Please check back soon.",
  provider_unavailable:
    "Jasmine does not have a bookable time available right now. Please check back soon.",
  source_order_inactive:
    "This visit is linked to an order that is no longer active. Please contact the care team for help.",
  source_order_unavailable:
    "This guided visit could not be linked to its treatment order. Please contact the care team for help.",
};

export function bookingUnavailableCopy(reason: string): string {
  return (
    BOOKING_UNAVAILABLE_COPY[reason] ??
    "Booking is not available right now. Please check back soon."
  );
}

/** Compact post-purchase rail for the paid nurse-guided injection visit. */
export function GuidedSelfInjectionCard({
  state,
}: GuidedSelfInjectionCardProps) {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const exposureTracked = useRef(false);

  useEffect(() => {
    if (exposureTracked.current) return;
    exposureTracked.current = true;
    track("guided_injection_portal_viewed", {
      experiment_key: state.experiment.code_key,
      experiment_sanity_key: state.experiment.key,
      variant_key: state.experiment.variant_key,
      entitlement_key: state.offering.entitlement_key,
      status: state.status,
      product_id: state.source?.product_id ?? null,
    });
  }, [
    state.experiment.code_key,
    state.experiment.key,
    state.experiment.variant_key,
    state.offering.entitlement_key,
    state.source?.product_id,
    state.status,
  ]);

  const clinicianName = PUBLIC_NURSE_NAME;
  const clinicianTitle = state.clinician?.title ?? "Registered Nurse";
  const clinicianPhoto = renderableClinicianPhoto(state.clinician?.photo_url);
  const status = state.status ?? "awaiting_delivery";
  const appointment = state.appointment;
  const canStartBooking =
    (status === "ready_to_book" || status === "awaiting_delivery") &&
    !state.booking_unavailable_reason;
  const openBooking = (isReschedule = false) => {
    setRescheduling(isReschedule);
    setBookingOpen(true);
    track("guided_injection_booking_started", {
      experiment_key: state.experiment.code_key,
      experiment_sanity_key: state.experiment.key,
      variant_key: state.experiment.variant_key,
      entitlement_key: state.offering.entitlement_key,
      mode: isReschedule ? "reschedule" : "book",
    });
  };

  const openDetails = () => {
    setDetailsOpen(true);
    track("guided_injection_appointment_opened", {
      entitlement_key: state.offering.entitlement_key,
    });
  };

  return (
    <section
      aria-labelledby="guided-self-injection-title"
      data-guided-injection-status={status}
    >
      <header className="flex items-center justify-between gap-10 bg-[#ECECEC] p-10 font-mono uppercase text-12 text-[#7C7C7C]">
        <div className="flex min-w-0 items-center gap-7">
          <Video size={12} strokeWidth={1.5} aria-hidden />
          <p className="truncate">NURSE-GUIDED SELF-INJECTION</p>
        </div>
        <StatusBadge status={status} />
      </header>

      <div className="flex items-stretch border border-line-subtle border-t-0 bg-raised">
        <div className="relative w-[10rem] shrink-0 self-stretch overflow-hidden bg-sunken">
          {clinicianPhoto ? (
            <img
              src={clinicianPhoto}
              alt={clinicianName}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center type-title-md text-ink-muted">
              {clinicianInitials(clinicianName)}
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex-1 p-15">
            <h2
              id="guided-self-injection-title"
              className="font-mono text-15 uppercase leading-110"
            >
              {state.presentation.title}
            </h2>
            <p className="mt-4 font-body text-13 text-[#7C7C7C]">
              A private video visit with {clinicianName}, {clinicianTitle}.
            </p>
            {state.source?.product_name && (
              <p className="mt-7 font-mono text-11 uppercase text-[#929292]">
                FOR {state.source.product_name}
              </p>
            )}

            {status === "scheduled" && appointment && (
              <ScheduledVisit appointment={appointment} />
            )}

            {status === "completed" && (
              <div className="mt-9 flex items-center gap-7 text-success">
                <Check size={15} strokeWidth={1.5} aria-hidden />
                <p className="type-label-sm">SESSION COMPLETE</p>
              </div>
            )}

            {status === "refunded" && (
              <p className="mt-8 font-body text-13 text-[#7C7C7C]">
                This guided visit is no longer available because its purchase
                was refunded.
              </p>
            )}

            {state.booking_unavailable_reason &&
              (status === "ready_to_book" ||
                status === "awaiting_delivery") && (
                <p role="status" className="mt-8 type-body-sm text-warning">
                  {bookingUnavailableCopy(state.booking_unavailable_reason)}
                </p>
              )}
          </div>

          {canStartBooking && (
            <button
              type="button"
              onClick={() => openBooking(false)}
              className="flex h-40 w-full items-center justify-between border-t border-line-subtle bg-black px-15 text-left text-white"
            >
              <span className="blink font-body text-14">Book appointment</span>
              <span
                aria-hidden
                className="blink inline-flex w-8 items-center justify-center"
              >
                <Icon name="Arrow" viewBox="0 0 10 12" />
              </span>
            </button>
          )}

          {status === "scheduled" && appointment && (
            <button
              type="button"
              onClick={openDetails}
              className="flex h-40 w-full items-center justify-between border-t border-line-subtle bg-black px-15 text-left font-body text-14 text-white"
            >
              <span>View appointment</span>
              <span
                aria-hidden
                className="inline-flex w-8 items-center justify-center"
              >
                <Icon name="Arrow" viewBox="0 0 10 12" />
              </span>
            </button>
          )}

          {status === "completed" && (
            <button
              type="button"
              onClick={openDetails}
              className="flex h-40 w-full items-center justify-between border-t border-line-subtle bg-[#D9D9D9] px-15 text-left font-body text-13 text-ink hover:bg-[#CCCCCC]"
            >
              <span>View visit details</span>
              <span
                aria-hidden
                className="inline-flex w-8 items-center justify-center"
              >
                <Icon name="Arrow" viewBox="0 0 10 12" />
              </span>
            </button>
          )}
        </div>
      </div>

      <GuidedInjectionDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        appointment={appointment}
        resource={state.preparation}
        clinicianName={clinicianName}
        onReschedule={() => {
          setDetailsOpen(false);
          openBooking(true);
        }}
      />
      <GuidedInjectionBookingDialog
        open={bookingOpen}
        onOpenChange={(next) => {
          setBookingOpen(next);
          if (!next) setRescheduling(false);
        }}
        clinicianName={clinicianName}
        clinicianAvatarUrl={state.clinician?.photo_url ?? null}
        expectations={state.presentation.expectations}
        earliestBookingAt={state.earliest_booking_at}
        experimentKey={state.experiment.code_key}
        variantKey={state.experiment.variant_key}
        entitlementKey={state.offering.entitlement_key}
        rescheduleAppointmentId={rescheduling ? appointment?.id : null}
      />
    </section>
  );
}

function StatusBadge({ status }: { status: GuidedSelfInjectionStatus }) {
  const labels: Record<GuidedSelfInjectionStatus, string> = {
    awaiting_delivery: "READY TO BOOK",
    ready_to_book: "READY TO BOOK",
    scheduled: "SCHEDULED",
    completed: "COMPLETED",
    refunded: "PURCHASE REFUNDED",
  };
  return (
    <span className="shrink-0 border border-line-subtle bg-raised px-8 py-4 font-mono text-10 uppercase leading-none text-ink-secondary">
      {labels[status]}
    </span>
  );
}

function ScheduledVisit({ appointment }: { appointment: Appointment }) {
  const start = parseISO(appointment.start_at);
  const end = appointment.end_at ? parseISO(appointment.end_at) : null;
  return (
    <div className="mt-9">
      <p className="font-body text-13 text-ink">
        {format(start, "EEEE, MMMM d · h:mm a")}
        {end ? `–${format(end, "h:mm a")}` : ""}
      </p>
    </div>
  );
}
